import { AuthStorage } from "utils/auth-storage";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button, DatePicker, Input, Tooltip, message } from "antd";
import { AllCommunityModule, ModuleRegistry } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { DeleteOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useParams } from "react-router-dom";
import { ProjectServices } from "apis/Services/ProjectService";
import styles from "./ProcurementLogPage.module.css";
import {
  mapScheduleImportRowForApi,
  normalizeScheduleCriticalValue,
  normalizeScheduleDateValue,
  normalizeScheduleImportRow,
  normalizeScheduleImportRows,
} from "./scheduleImportUtils";

ModuleRegistry.registerModules([AllCommunityModule]);

const modules = [AllCommunityModule];

const IMPORT_SELECTION_FIELD = "__importReviewSelection";
const DATE_COLUMN_FIELDS = new Set(["startDate", "finishDate"]);
const NUMERIC_ONLY_FIELDS = new Set(["totalFloat"]);
const CRITICAL_ALLOWED_VALUES = new Set(["true", "false"]);

const SCHEDULE_IMPORT_REVIEW_COLUMNS = [
  {
    headerName: "ID",
    field: "scheduleID",
    minWidth: 150,
    type: "text",
  },
  {
    headerName: "Start Date",
    field: "startDate",
    minWidth: 180,
    type: "date",
  },
  {
    headerName: "Finish Date",
    field: "finishDate",
    minWidth: 180,
    type: "date",
  },
  {
    headerName: "Task Name",
    field: "taskName",
    minWidth: 320,
    type: "text",
  },
  {
    headerName: "Total Float",
    field: "totalFloat",
    minWidth: 150,
    type: "text",
  },
  {
    headerName: "Critical",
    field: "critical",
    minWidth: 130,
    type: "text",
  },
];

function parseReviewDateValue(value) {
  const normalizedValue = normalizeScheduleDateValue(value);

  if (!normalizedValue) {
    return null;
  }

  const mmDdYyyyMatch = normalizedValue.match(/^(\d{2})-(\d{2})-(\d{4})$/);
  if (mmDdYyyyMatch) {
    const [, month, day, year] = mmDdYyyyMatch;
    const parsedDate = dayjs(`${year}-${month}-${day}`);
    return parsedDate.isValid() ? parsedDate : null;
  }

  return null;
}

function isStrictMmDdYyyy(value) {
  const normalizedValue = String(value || "").trim();

  if (!normalizedValue) {
    return true;
  }

  const mmDdYyyyMatch = normalizedValue.match(/^(\d{2})-(\d{2})-(\d{4})$/);
  if (!mmDdYyyyMatch) {
    return false;
  }

  const [, month, day, year] = mmDdYyyyMatch;
  const parsedDate = dayjs(`${year}-${month}-${day}`);

  return (
    parsedDate.isValid() && parsedDate.format("MM-DD-YYYY") === normalizedValue
  );
}

function isNumericOnlyValue(value) {
  const normalizedValue = String(value ?? "").trim();

  if (!normalizedValue) {
    return true;
  }

  return /^-?\d+(\.\d+)?$/.test(normalizedValue);
}

function isValidCriticalValue(value) {
  const normalizedValue = normalizeScheduleCriticalValue(value);

  if (!normalizedValue) {
    return true;
  }

  return CRITICAL_ALLOWED_VALUES.has(normalizedValue);
}

function getCellValidationMessage(row, field) {
  const value = row?.[field];

  if (DATE_COLUMN_FIELDS.has(field) && !isStrictMmDdYyyy(value)) {
    return "Invalid date format. Use MM-DD-YYYY.";
  }

  if (NUMERIC_ONLY_FIELDS.has(field) && !isNumericOnlyValue(value)) {
    return "Only numeric values are allowed.";
  }

  if (field === "critical" && !isValidCriticalValue(value)) {
    return "Use true or false.";
  }

  return "";
}

function getRowValidationMessages(row) {
  return SCHEDULE_IMPORT_REVIEW_COLUMNS.flatMap((columnConfig) => {
    const validationMessage = getCellValidationMessage(row, columnConfig.field);

    return validationMessage
      ? [`${columnConfig.headerName}: ${validationMessage}`]
      : [];
  });
}

function getMergedRowErrorMessages(row) {
  const localValidationMessages = getRowValidationMessages(row);
  const backendErrorMessages = Array.isArray(row?.errorMessages)
    ? row.errorMessages
    : [];

  return [...new Set([...localValidationMessages, ...backendErrorMessages])];
}

function getValidationCellStyle(row, field) {
  if (!getCellValidationMessage(row, field)) {
    return {
      backgroundColor: "",
      color: "",
    };
  }

  return {
    backgroundColor: "#fff2f0",
    color: "#cf1322",
  };
}

function getValidationRowStyle(row) {
  return getMergedRowErrorMessages(row).length > 0
    ? { backgroundColor: "#fffaf9" }
    : { backgroundColor: "" };
}

const TextEditor = ({ value, onValueChange, stopEditing }) => (
  <Input
    autoFocus
    variant="borderless"
    defaultValue={value}
    onChange={(event) => onValueChange(event.target.value)}
    onBlur={() => stopEditing()}
    onPressEnter={() => stopEditing()}
    style={{ width: "100%", height: "100%", fontSize: "13px" }}
  />
);

const ReviewDateEditor = ({ value, onValueChange, stopEditing }) => {
  const pickerValue = useMemo(() => parseReviewDateValue(value), [value]);

  return (
    <div className="procurementDateEditor">
      <DatePicker
        autoFocus
        open={true}
        allowClear
        inputReadOnly
        size="large"
        value={pickerValue}
        format="MM-DD-YYYY"
        onChange={(dateValue) => {
          onValueChange(dateValue ? dateValue.format("MM-DD-YYYY") : "");
          stopEditing();
        }}
        onOpenChange={(isOpen) => {
          if (!isOpen) {
            stopEditing();
          }
        }}
        popupStyle={{ zIndex: 2200 }}
        style={{ width: "100%", height: "100%" }}
      />
    </div>
  );
};

function getInitialSelectedView(acceptedRows, rejectedRows) {
  if (acceptedRows.length > 0) {
    return "accepted";
  }

  if (rejectedRows.length > 0) {
    return "issues";
  }

  return "all";
}

function getViewButtonClassName(selectedView, viewKey) {
  return `${styles.antBtn} ${
    selectedView === viewKey ? styles.quickFilterButtonActive : ""
  }`;
}

function getScheduleIdNoun(count) {
  return Number(count) === 1 ? "Schedule ID" : "Schedule IDs";
}

function getImportedScheduleIdsMessage(count) {
  return `${count} ${getScheduleIdNoun(count)} ${
    Number(count) === 1 ? "was" : "were"
  } imported successfully.`;
}

function getScheduleIdsWithIssuesMessage(count) {
  return `${count} ${getScheduleIdNoun(count)} still ${
    Number(count) === 1 ? "contains" : "contain"
  } issues and ${Number(count) === 1 ? "was" : "were"} not imported.`;
}

function ReviewScheduleFile({
  rejectedData = [],
  acceptedData = [],
  handleBack,
  handleCancel,
  setAcceptedData,
  setRejectedData,
  onImportSuccess = () => {},
  beginSyncActivity,
  finishSyncActivity,
  failSyncActivity,
}) {
  const { company_id, id } = useParams();
  const normalizedAcceptedData = useMemo(
    () => normalizeScheduleImportRows(acceptedData),
    [acceptedData],
  );
  const normalizedRejectedData = useMemo(
    () => normalizeScheduleImportRows(rejectedData),
    [rejectedData],
  );
  const [selectedView, setSelectedView] = useState(() =>
    getInitialSelectedView(normalizedAcceptedData, normalizedRejectedData),
  );
  const [selectedRows, setSelectedRows] = useState([]);
  const [importing, setImporting] = useState(false);
  const gridRef = useRef(null);

  const allData = useMemo(
    () => [...normalizedAcceptedData, ...normalizedRejectedData],
    [normalizedAcceptedData, normalizedRejectedData],
  );

  const tableData = useMemo(() => {
    if (selectedView === "accepted") {
      return normalizedAcceptedData;
    }

    if (selectedView === "issues") {
      return normalizedRejectedData;
    }

    return allData;
  }, [allData, normalizedAcceptedData, normalizedRejectedData, selectedView]);

  const columnDefs = useMemo(
    () => [
      {
        field: IMPORT_SELECTION_FIELD,
        colId: IMPORT_SELECTION_FIELD,
        headerName: "",
        headerCheckboxSelection: true,
        checkboxSelection: true,
        pinned: "left",
        lockPinned: true,
        lockPosition: "left",
        width: 108,
        minWidth: 108,
        maxWidth: 108,
        sortable: false,
        filter: false,
        resizable: false,
        suppressMovable: true,
        editable: false,
        cellRenderer: (params) => {
          const lineNumber = params.data?.lineCnt || params.node.rowIndex + 1;
          const errors = getMergedRowErrorMessages(params.data);
          const hasErrors = errors.length > 0;

          return (
            <div className={styles.importReviewSelectionCell}>
              <span className={styles.importReviewLineNumber}>
                {lineNumber}
              </span>
              {hasErrors ? (
                <Tooltip
                  title={
                    <div>
                      {errors.map((errorMessage, index) => (
                        <div key={`${errorMessage}-${index}`}>
                          • {errorMessage}
                        </div>
                      ))}
                    </div>
                  }
                  color="#1f1f1f"
                >
                  <ExclamationCircleOutlined
                    style={{
                      color: "#ff4d4f",
                      fontSize: 16,
                      cursor: "pointer",
                    }}
                  />
                </Tooltip>
              ) : null}
            </div>
          );
        },
      },
      ...SCHEDULE_IMPORT_REVIEW_COLUMNS.map((columnConfig) => ({
        headerName: columnConfig.headerName,
        field: columnConfig.field,
        minWidth: columnConfig.minWidth,
        editable: true,
        cellEditor:
          columnConfig.type === "date" ? ReviewDateEditor : TextEditor,
        valueParser: (params) =>
          columnConfig.type === "date"
            ? normalizeScheduleDateValue(params.newValue)
            : columnConfig.field === "critical"
              ? normalizeScheduleCriticalValue(params.newValue)
              : String(params.newValue ?? "").trim(),
        valueFormatter:
          columnConfig.type === "date"
            ? (params) => normalizeScheduleDateValue(params.value)
            : undefined,
        tooltipValueGetter: (params) =>
          getCellValidationMessage(params.data, columnConfig.field) || null,
        cellStyle: (params) =>
          getValidationCellStyle(params.data, columnConfig.field),
      })),
    ],
    [],
  );

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: true,
      resizable: true,
      suppressMovable: true,
    }),
    [],
  );

  useEffect(() => {
    if (selectedView !== "all") {
      return;
    }

    if (allData.length === 0) {
      return;
    }

    gridRef.current?.api?.refreshCells({ force: true });
  }, [allData.length, selectedView]);

  useEffect(() => {
    if (!gridRef.current?.api) {
      return;
    }

    gridRef.current.api.deselectAll();
    setSelectedRows([]);
    gridRef.current.api.refreshCells({ force: true });
  }, [selectedView, tableData]);

  const handleImport = () => {
    gridRef.current?.api?.stopEditing();
    setImporting(true);

    const code = AuthStorage.getItem("token");
    const loginEmail = AuthStorage.getItem("email");
    const updatedRows = allData.map((row, index) =>
      normalizeScheduleImportRow(row, index),
    );

    const requestData = {
      company_id,
      project_id: id,
      code,
      loginEmail,
    };
    const formData = new FormData();

    formData.append("f_schedule", new Blob([]), "");
    formData.append("saveFlag", "1");
    formData.append(
      "scheduleRows",
      JSON.stringify(updatedRows.map(mapScheduleImportRowForApi)),
    );
    const syncToken = beginSyncActivity?.("Importing schedule");

    ProjectServices.uploadScheduleFileForReview(requestData, formData)
      .then((response) =>
        typeof response?.json === "function" ? response.json() : response,
      )
      .then((res) => {
        const acceptedItemsCnt =
          Number(res?.acceptedItemsCnt) ||
          updatedRows.length - (res?.invalidItems?.length || 0);
        const invalidItems = normalizeScheduleImportRows(res?.invalidItems);

        if (invalidItems.length > 0) {
          if (acceptedItemsCnt > 0) {
            onImportSuccess?.();
          }

          message.warning(
            `${getImportedScheduleIdsMessage(
              acceptedItemsCnt,
            )} ${getScheduleIdsWithIssuesMessage(
              invalidItems.length,
            )} Please review them below.`,
            6,
          );
          setAcceptedData([]);
          setRejectedData(invalidItems);
          setSelectedView("issues");
          finishSyncActivity?.(syncToken, "Schedule import completed");
          return;
        }

        message.success("All Schedule IDs imported successfully!");
        onImportSuccess?.();
        finishSyncActivity?.(syncToken, "Schedule imported");
        handleCancel?.();
      })
      .catch((error) => {
        console.error(error);
        message.error(
          error?.message || "Failed to import the schedule. Please try again.",
        );
        failSyncActivity?.(
          syncToken,
          "Failed to import the schedule. You may no longer be synced to the latest version.",
        );
      })
      .finally(() => {
        setImporting(false);
      });
  };

  const handleSelectionChanged = () => {
    const selected = gridRef.current?.api?.getSelectedRows?.() || [];
    setSelectedRows(selected);
  };

  const handleDeleteSelectedRows = () => {
    const selectedLineIds = new Set(
      selectedRows.map((row) => String(row.lineCnt)),
    );

    let nextAcceptedRows = normalizedAcceptedData;
    let nextRejectedRows = normalizedRejectedData;

    if (selectedView === "accepted") {
      nextAcceptedRows = normalizedAcceptedData.filter(
        (row) => !selectedLineIds.has(String(row.lineCnt)),
      );
    } else if (selectedView === "issues") {
      nextRejectedRows = normalizedRejectedData.filter(
        (row) => !selectedLineIds.has(String(row.lineCnt)),
      );
    } else {
      nextAcceptedRows = normalizedAcceptedData.filter(
        (row) => !selectedLineIds.has(String(row.lineCnt)),
      );
      nextRejectedRows = normalizedRejectedData.filter(
        (row) => !selectedLineIds.has(String(row.lineCnt)),
      );
    }

    setAcceptedData(nextAcceptedRows);
    setRejectedData(nextRejectedRows);
    message.success("Selected Schedule IDs removed successfully.");
  };

  return (
    <div className={`${styles.tableCard} ${styles.importReviewCard}`}>
      <div className={`${styles.quickBar} ${styles.importReviewQuickBar}`}>
        <div className={styles.importReviewToolbarLeft}>
          <div className={styles.importReviewHeadingGroup} />

          <div className={styles.importReviewViewButtons}>
            <Button
              className={getViewButtonClassName(selectedView, "all")}
              onClick={() => setSelectedView("all")}
            >
              All Schedule Line Items
              <span
                className={`${styles.importReviewTabCount} ${
                  selectedView === "all"
                    ? styles.importReviewTabCountActive
                    : ""
                }`}
              >
                {allData.length}
              </span>
            </Button>

            <Button
              className={getViewButtonClassName(selectedView, "accepted")}
              onClick={() => setSelectedView("accepted")}
            >
              Accepted Schedule Line Items
              <span
                className={`${styles.importReviewTabCount} ${
                  selectedView === "accepted"
                    ? styles.importReviewTabCountActive
                    : ""
                }`}
              >
                {normalizedAcceptedData.length}
              </span>
            </Button>

            <Button
              className={getViewButtonClassName(selectedView, "issues")}
              onClick={() => setSelectedView("issues")}
            >
              Schedule Line Items with Issues
              <span
                className={`${styles.importReviewTabCount} ${
                  selectedView === "issues"
                    ? styles.importReviewTabCountActive
                    : ""
                }`}
              >
                {normalizedRejectedData.length}
              </span>
            </Button>
          </div>
        </div>

        <div className={styles.importReviewToolbarRight}>
          {selectedRows.length > 0 ? (
            <span className={styles.selectionBadge}>
              {selectedRows.length} selected
            </span>
          ) : null}
        </div>
      </div>

      <div
        className={`${styles.gridWrapper} ${styles.importReviewGridWrapper}`}
      >
        <div
          className={`ag-theme-alpine w-100 h-100 ${styles.grid} ${styles.importReviewGrid}`}
        >
          <AgGridReact
            ref={gridRef}
            modules={modules}
            rowData={tableData}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            rowSelection="multiple"
            suppressRowClickSelection={true}
            suppressDragLeaveHidesColumns={true}
            animateRows={false}
            editType="singleCell"
            singleClickEdit={true}
            domLayout="normal"
            enableBrowserTooltips={true}
            tooltipShowDelay={100}
            headerHeight={60}
            rowHeight={48}
            overlayNoRowsTemplate="<span>No Schedule IDs found in this view</span>"
            onSelectionChanged={handleSelectionChanged}
            getRowId={(params) =>
              String(
                params.data?.lineCnt ??
                  params.data?.scheduleID ??
                  params.data?.taskName ??
                  params.rowIndex,
              )
            }
            getRowStyle={(params) => getValidationRowStyle(params.data)}
            onCellClicked={(params) => {
              if (params.colDef.field === IMPORT_SELECTION_FIELD) {
                const isSelected = params.node.isSelected();
                params.node.setSelected(!isSelected);
              }
            }}
            onCellValueChanged={(params) => {
              params.data[params.colDef.field] = DATE_COLUMN_FIELDS.has(
                params.colDef.field,
              )
                ? normalizeScheduleDateValue(params.newValue)
                : params.colDef.field === "critical"
                  ? normalizeScheduleCriticalValue(params.newValue)
                  : String(params.newValue ?? "").trim();

              gridRef.current?.api?.refreshCells({
                rowNodes: [params.node],
                force: true,
              });
              gridRef.current?.api?.redrawRows({
                rowNodes: [params.node],
              });
            }}
          />
        </div>
      </div>

      <div className={styles.importReviewFooter}>
        <div className={styles.importReviewFooterActions}>
          <Button
            className={styles.antBtn}
            disabled={importing}
            onClick={handleBack}
          >
            Back
          </Button>

          <Button className={styles.antBtn} onClick={handleCancel}>
            Cancel
          </Button>

          <Button
            danger
            className={styles.importReviewDangerButton}
            disabled={selectedRows.length < 1}
            onClick={handleDeleteSelectedRows}
          >
            <DeleteOutlined />
            Delete Selected
          </Button>
        </div>

        <Button
          type="primary"
          className={styles.importReviewPrimaryButton}
          loading={importing}
          onClick={handleImport}
          disabled={allData.length < 1}
        >
          Import Schedule Line Items
        </Button>
      </div>
    </div>
  );
}

export default ReviewScheduleFile;
